import { Icon, Toast } from '@fattureincloud/fic-design-system'
import {
  faLongArrowAltRight,
  faSearch,
  faTimesCircle,
} from '@fortawesome/pro-light-svg-icons'
import parse from 'html-react-parser'
import React, { FC, useEffect, useRef, useState } from 'react'
import Autocomplete from 'react-autocomplete'

import noResults from '../../assets/img/no-results.png'
import { httpRequest, ResponseInterface } from '../../common/network'
import { errorMessage, locale } from '../../locales/locale'
import { useAppSelector } from '../../redux/hooks'
import { SearchResult } from '../definitions'
import {
  AutocompleteContainer,
  AutocompleteStyled,
  Option,
  Results,
  TopArticles,
  Tree,
} from './helpautocomplete.components'
interface Props {
  onSelect: (permalink: string, type: string) => void
  internal: boolean
  onOpen: (state: boolean) => void
  isExternal: boolean
  className?: string
  onChange?: () => void
}

const HelpAutocomplete: FC<Props> = (props: Props) => {
  const [filter, setFilter] = useState<string>('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>(undefined)
  const [open, setOpen] = useState<boolean>(false)
  const [topArticles, setTopArticles] = useState<SearchResult[]>([])
  const autocompleteRef = useRef<Autocomplete>()
  const realm = useAppSelector((state) => state.realm.value)
  const editorModeActive = useAppSelector((state) => state.editor.value)
  const adminMode = useAppSelector((state) => state.adminMode.value)
  const visualizationMode = useAppSelector(
    (state) => state.visualizationMode.value
  )

  useEffect(() => {
    getTopArticles()
  }, [])

  const search = (filter: string) => {
    if (filter === null) {
      return
    }
    const minChars = editorModeActive || adminMode ? 2 : 3
    if (filter.length < minChars) {
      setSearchResults(undefined)
      return
    }

    //search enabled only for enter key press
    if (filter.charAt(0) == '@' || filter.charAt(0) == '$')
      return

    httpRequest({
      method: 'GET',
      path: 'help/search',
      params: {
        edit_mode: editorModeActive ? 1 : 0,
        query: filter,
        realm: realm,
        visualize_mode: adminMode ? visualizationMode : 0,
      },
      onSuccess: (response: { data: SearchResult[] }) =>
        setSearchResults(response.data),
      onError: (res: ResponseInterface) => {
        if (editorModeActive || adminMode)
          Toast.error(errorMessage, { content: res?.message ?? errorMessage })
        setSearchResults(undefined)
      },
    })
  }

  const handleChange = (name: string, value: string) => {
    let newValue = value.toString()
    newValue = newValue.replace(/<[^>]*>/g, '')
    if (!newValue || newValue === 'undefined')
      newValue = ''
    setFilter(newValue)
  }

  const handleSelect = (label: string, data: SearchResult) => {
    props.onSelect(data.permalink ?? data.tree.permalink, data.type ?? 'articolo')
    changeSize()
  }

  const cleanLeftZeroes = (filter: string) => {
    return filter.replace(/^@0+/, '@').replace(/^\$0+/, '$')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {

    if ((filter.charAt(0) == '@' || filter.charAt(0) == '$') && e.key === 'Enter') {
      httpRequest({
        method: 'GET',
        path: 'help/search',
        params: {
          edit_mode: editorModeActive ? 1 : 0,
          query: cleanLeftZeroes(filter),
          realm: realm,
          visualize_mode: adminMode ? visualizationMode : 0,
        },
        onSuccess: (response: { data: SearchResult[] }) => {
          setSearchResults(response.data)
          const element: HTMLElement = document.getElementsByClassName('fic-input-text-input')[0] as HTMLElement
          element.click()
        },
        onError: (res: ResponseInterface) => {
          if (editorModeActive || adminMode)
            Toast.error(errorMessage, { content: res?.message ?? errorMessage })
          setSearchResults(undefined)
        },
      })

    }
    else if (e.key === 'Enter' && autocompleteRef.current) {
      setOpen(true)
    }
  }

  const renderItem = (item: SearchResult) => {
    return (
      <Option type={item.type} key={item.id}>
        {editorModeActive && <>{item.id} - </>}
        {parse(item.title ?? item.label)}
        {item.tree && (
          <Tree>
            {item.tree.section3_title && (
              <>
                <span>
                  {editorModeActive && <>{item.tree.section3_id} - </>}
                  {item.tree.section3_title}
                </span>
                <Icon icon={faLongArrowAltRight} />
              </>
            )}
            {item.tree.section2_title && (
              <>
                <span>
                  {editorModeActive && <>{item.tree.section2_id} - </>}
                  {item.tree.section2_title}
                </span>
                <Icon icon={faLongArrowAltRight} />
              </>
            )}
            {item.tree.section1_title && (
              <span>
                {editorModeActive && <>{item.tree.section1_id} - </>}
                {item.tree.section1_title}
              </span>
            )}
          </Tree>
        )}
      </Option>
    )
  }

  const changeSize = () => {
    setOpen(!open)
    if (!open) {
      setTimeout(() => {
        if (autocompleteRef.current) autocompleteRef.current.focus()
      }, 200)
    }
    if (props.onOpen) {
      props.onOpen(!open)
    }
  }

  const getTopArticles = () => {
    httpRequest({
      method: 'GET',
      path: 'help/top_articles',
      params: { realm: realm },
      onSuccess: (response: { data: SearchResult[] }) => {
        setTopArticles(response.data)
      },
    })
  }

  const renderMenuContent = (items: React.ReactNode[]) => {
    return (
      <Results open={open} internal={props.internal}>
        {searchResults ? (
          searchResults.length > 0 ? (
            <div className='searchResultsHeader'>
              <p>{locale('Risultati della ricerca')}</p>
            </div>
          ) : (
            <div className='noResults'>
              <p>{locale('Nessun risultato!')}</p>
              <img alt='noresults' srcSet={noResults + ' 3x'} />
            </div>
          )
        ) : (
          <></>
        )}
        {items.length > 0 && items}
        {topArticles.length > 0 && (
          <TopArticles>
            <p>{locale('Ti potrebbe interessare')}</p>
            <ul>
              {topArticles.map((article, idx) => {
                return (
                  <li
                    key={idx}
                    onClick={() => handleSelect(undefined, article)}
                  >
                    <span>{article.title}</span>
                  </li>
                )
              })}
            </ul>
          </TopArticles>
        )}
      </Results>
    )
  }

  let placeholder = props.internal
    ? locale('Cerca in ') + locale('Fatture in Cloud')
    : locale('Cosa stai cercando?') + ' (' + locale('Fatturazione elettronica, prima nota') + ', etc.)'
  if (realm === 'dic') {
    placeholder = props.internal
      ? locale('Cerca in ') + locale('Dipendenti in Cloud')
      : locale('Cosa stai cercando?') + ' (' + locale('Giustificativi, dipendente') + ', etc.)'
  }
  if (realm === 'dic-es') { //TODO check if this is correct
    placeholder = props.internal
      ? locale('Cerca in ') + locale('Dipendenti in Cloud')
      : locale('Cosa stai cercando?') + ' (' + locale('Giustificativi, dipendente') + ', etc.)'
  }
  if (realm === 'resellers') {
    placeholder = props.internal
      ? locale('Cerca in ') + locale('Resellers')
      : locale('Cosa stai cercando?') + ' (' + locale('Pacchetti, rinnovi, coupon') + ', etc.)'
  }

  return (
    <AutocompleteContainer
      internal={props.internal}
      open={open}
      isExternal={props.isExternal}
      className={(open ? 'open ' : '') + props.className}
    >
      <Icon
        style={{ marginRight: '10px', zIndex: '10000' }}
        icon={faSearch}
        onClick={props.internal ? () => changeSize() : null}
      />
      <AutocompleteStyled
        ref={autocompleteRef}
        placeholder={placeholder}
        value={(!filter || filter === 'undefined') ? '' : filter}
        options={searchResults ? searchResults : []}
        loadOptions={search}
        loadOptionsDelay={200}
        renderItem={renderItem}
        onSelect={handleSelect}
        onChange={handleChange}
        renderMenuContent={renderMenuContent}
        internal={props.internal}
        open={open}
        shouldItemRender={() => {
          return true
        }}
        onKeyPress={handleKeyPress}
      />
      {props.internal && (
        <Icon
          icon={faTimesCircle}
          className='close'
          onClick={props.internal ? () => changeSize() : null}
        />
      )}
    </AutocompleteContainer>
  )
}
export default HelpAutocomplete
